/* Chat con el colaborador seleccionado. La sesión vive en el companion (persistida en la
   carpeta del proyecto). El chat DELIBERA: el documento solo se toca con el botón
   "Insertar como control de cambios" de cada propuesta. */
import { useState, useEffect, useRef } from "react";
import type { AgentInfo } from "./agents";
import { avatarFor } from "./avatars";
import { COMPANION, companionError, providerError } from "./companion";
import { replaceSelectionTracked, getDocumentUrl } from "./office";
import { esDePartida, fraseDeNoAqui, nombreDe, rutaLocal } from "./guionLogica";
/** Un manuscrito con su versión: ahí lo que propone el coach puede ir a su escena. */
const ES_MANUSCRITO = /-m\d{1,2}-v\d{1,3}\.docx$/i;
import { logRewrite } from "./companion";
import { askConfirm } from "./Confirm";
import Formato, { CSS_DEL_FORMATO } from "./Formato";

interface Msg {
  role: "user" | "assistant";
  content: string;
  at?: number;
}

/** Separa el bloque <<<PROPUESTA … PROPUESTA>>> del resto del mensaje. */
function splitProposal(content: string): { text: string; proposal: string | null } {
  const m = content.match(/<<<PROPUESTA\s*\n([\s\S]*?)\nPROPUESTA>>>/);
  if (!m) return { text: content, proposal: null };
  return { text: content.replace(m[0], "").trim(), proposal: m[1].trim() };
}

export default function Chat({
  agent,
  projectDir,
  model,
  contextFiles,
}: {
  agent: AgentInfo | undefined;
  projectDir: string;
  model: string;
  contextFiles: string[];
}) {
  const [history, setHistory] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [inserted, setInserted] = useState<number | null>(null);
  // En qué documento entraría lo que se inserta: el botón lo dice (21-oct: entró en la biblia sin que se viera dónde).
  const [enDoc, setEnDoc] = useState("");
  const [docRuta, setDocRuta] = useState("");
  // Las escenas del guion, para llevar lo que proponga el coach a la suya (21-oct). Vacío: no hay guion o no es un manuscrito.
  const [escenas, setEscenas] = useState<string[]>([]);
  const [reciente, setReciente] = useState<string | null>(null);
  const [aEscena, setAEscena] = useState<Record<number, string>>({});
  const [llevado, setLlevado] = useState<Record<number, { estado: "pidiendo" } | { estado: "hecho"; texto: string } | { estado: "error"; texto: string }>>({});
  // Desde cuándo piensa el colaborador: una respuesta larga tarda minutos, y se dice cuánto lleva.
  const [desde, setDesde] = useState<number | null>(null);
  const [ahora, setAhora] = useState(Date.now());
  const [showInstr, setShowInstr] = useState(false);
  const [instr, setInstr] = useState("");
  const [instrMsg, setInstrMsg] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  const qs = () => `agent=${encodeURIComponent(agent?.id ?? "")}&dir=${encodeURIComponent(projectDir)}`;

  useEffect(() => {
    if (!agent) return;
    setErr("");
    fetch(`${COMPANION}/chat-session?${qs()}`)
      .then((r) => r.json())
      .then((j) => setHistory(j.history ?? []))
      .catch(() => setHistory([]));
    fetch(`${COMPANION}/instructions?${qs()}`)
      .then((r) => r.json())
      .then((j) => setInstr(j.text ?? ""))
      .catch(() => setInstr(""));
  }, [agent?.id, projectDir]);
  // El nombre del documento abierto, para el botón de insertar. Solo se pregunta dónde está guardado.
  useEffect(() => {
    void getDocumentUrl().then(async (u) => {
      const ruta = rutaLocal(u) ?? "";
      setEnDoc(nombreDe(ruta || u)); setDocRuta(ruta);
      // En un manuscrito, las escenas de su guion: lo que proponga el coach se lleva a una de ellas.
      if (!ruta || !ES_MANUSCRITO.test(nombreDe(ruta))) { setEscenas([]); return; }
      try {
        const i = await (await fetch(`${COMPANION}/guion?dir=${encodeURIComponent(projectDir)}&doc=${encodeURIComponent(ruta)}`)).json();
        setEscenas(i?.hay ? (i.capitulos ?? []).flatMap((c: { escenas?: { numero: string }[] }) => (c.escenas ?? []).map((e) => e.numero)) : []);
        setReciente(i?.escena_reciente ?? null);
      } catch { setEscenas([]); }
    }).catch(() => { setEnDoc(""); setDocRuta(""); setEscenas([]); });
  }, [projectDir]);
  useEffect(() => {
    if (!sending) return;
    const reloj = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(reloj);
  }, [sending]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [history, sending]);

  async function send() {
    const text = input.trim();
    if (!text || !agent || sending) return;
    setErr("");
    setSending(true);
    setDesde(Date.now()); setAhora(Date.now());
    setInput("");
    setHistory((h) => [...h, { role: "user", content: text }]);
    try {
      const r = await fetch(`${COMPANION}/chat-session?${qs()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, model, contextFiles }),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => "");
        // Dicho para el autor, no el JSON en crudo (10-oct: «403: {"error":{"message":"OpenRouter ha rechazado…»).
        throw Object.assign(new Error(providerError("El chat", r.status, t)), { dicho: true });
      }
      const j = await r.json();
      setHistory(j.history ?? []);
    } catch (e: any) {
      setErr(e?.dicho ? String(e.message) : companionError(e));
    } finally {
      setSending(false);
      setDesde(null);
    }
  }

  async function resetChat() {
    if (!agent) return;
    if (!(await askConfirm(`¿Borrar la conversación con ${agent.name} de este proyecto?`))) return;
    await fetch(`${COMPANION}/chat-session?${qs()}`, { method: "DELETE" }).catch(() => {});
    setHistory([]);
  }

  async function saveInstr() {
    if (!agent) return;
    setInstrMsg("Guardando…");
    try {
      await fetch(`${COMPANION}/instructions?${qs()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: instr }),
      });
      setInstrMsg("✓ Guardadas — el colaborador las aplicará a partir del próximo mensaje.");
    } catch {
      setInstrMsg("No se pudieron guardar.");
    }
  }

  async function insert(i: number, proposal: string) {
    try {
      // Antes de escribir: en qué documento. En la biblia, el guion, la escaleta o el estilo, no.
      let nombre = "";
      try { const u = await getDocumentUrl(); nombre = nombreDe(rutaLocal(u) ?? u); } catch { /* sin nombre: no se puede saber */ }
      setEnDoc(nombre);
      if (esDePartida(nombre)) { setErr(fraseDeNoAqui(nombre)); return; }
      setErr("");
      await replaceSelectionTracked(proposal);
      logRewrite(proposal);
      setInserted(i);
      setTimeout(() => setInserted(null), 2500);
    } catch (e: any) {
      setErr("No se pudo insertar: " + (e?.message ?? e));
    }
  }

  /** La escena a la que iría la propuesta del mensaje `i`: la elegida, la que se nombra en lo que pediste, la última en la que estabas o la primera. */
  const escenaPara = (i: number, proposal: string) => {
    if (aEscena[i]) return aEscena[i];
    const pedida = [history[i - 1]?.content ?? "", proposal].map((t) => (t.match(/\b(\d{1,3}\.\d{1,2})\b/) ?? [])[1]).find((n) => n && escenas.includes(n));
    return pedida ?? (reciente && escenas.includes(reciente) ? reciente : escenas[0]);
  };
  /** No escribe en Word: el motor crea la versión siguiente del manuscrito con la escena en su sitio, como cambio marcado. */
  async function llevarAEscena(i: number, escena: string, at: number | undefined) {
    if (!agent || !docRuta || !at) return;
    setLlevado((x) => ({ ...x, [i]: { estado: "pidiendo" } }));
    try {
      const r = await fetch(`${COMPANION}/guion/llevar-propuesta`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dir: projectDir, doc: docRuta, escena, agente: agent.id, at }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error ?? "No ha salido.");
      setLlevado((x) => ({ ...x, [i]: { estado: "hecho", texto: `Creada «${j.version}», con la escena ${j.escena} como cambio marcado (${j.palabras} palabras).${j.abierto ? " Se está abriendo en Word." : " Ábrela desde la carpeta del libro."} Acéptala, cámbiala o recházala en Revisar.` } }));
    } catch (e: any) {
      setLlevado((x) => ({ ...x, [i]: { estado: "error", texto: String(e?.message ?? e) } }));
    }
  }

  if (!agent) {
    return <div style={{ fontSize: 12, color: "var(--hi-tenue)", padding: "18px 0" }}>Cargando el roster de colaboradores…</div>;
  }

  return (
    <div>
      <style>{CSS_DEL_FORMATO}</style>
      {/* Identidad: con quién estás hablando */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "var(--hi-papel)", border: "1px solid var(--hi-raya)", borderRadius: 10, marginBottom: 10 }}>
        {avatarFor(agent.id) ? (
          <img src={avatarFor(agent.id)!} alt={agent.name} style={{ width: 34, height: 34, borderRadius: 999, objectFit: "cover", flex: "none" }} />
        ) : (
          <div style={{ width: 34, height: 34, borderRadius: 999, background: "var(--hi-humano)", color: "var(--hi-sobre-acento)", fontWeight: 800, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
            {agent.name.slice(0, 1)}
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 13 }}>{agent.name} <span style={{ color: "var(--hi-tenue)", fontWeight: 400 }}>· {agent.phase}</span></div>
          <div style={{ fontSize: 11, color: "var(--hi-tenue)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{agent.goal}</div>
        </div>
      </div>

      {/* Conversación */}
      <div style={{ maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, padding: "2px 0" }}>
        {history.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--hi-apagado)", padding: "14px 4px" }}>
            Habla con {agent.name} sobre tu proyecto. Cuando te proponga texto, tendrás un botón para
            insertarlo como control de cambios — nada entra al manuscrito sin tu clic.
          </div>
        )}
        {history.map((m, i) => {
          const { text, proposal } = m.role === "assistant" ? splitProposal(m.content) : { text: m.content, proposal: null };
          return (
            <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "92%" }}>
              <div style={{
                background: m.role === "user" ? "var(--hi-papel-2)" : "var(--hi-papel)",
                border: "1px solid var(--hi-raya)",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 12.5,
                whiteSpace: "pre-wrap",
                lineHeight: 1.5,
              }}>
                {m.role === "assistant" ? <Formato texto={text} /> : text}
                {proposal && (
                  <div style={{ marginTop: text ? 8 : 0, background: "var(--hi-fondo)", border: "1px dashed var(--hi-humano)", borderRadius: 8, padding: "8px 10px" }}>
                    <div style={{ whiteSpace: "pre-wrap" }}>{proposal}</div>
                    {/* En un manuscrito con guion: a SU escena, por el motor (no por el cursor). Lo del cursor queda como segunda opción. */}
                    {escenas.length > 0 && m.at && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                          <button onClick={() => void llevarAEscena(i, escenaPara(i, proposal), m.at)} disabled={llevado[i]?.estado === "pidiendo"}
                            style={{ background: "var(--hi-oficio)", color: "var(--hi-sobre-acento)", fontWeight: 800, border: 0, borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>
                            {llevado[i]?.estado === "pidiendo" ? "Llevándola…" : `Llevarla a la escena ${escenaPara(i, proposal)}`}
                          </button>
                          <select value={escenaPara(i, proposal)} onChange={(e) => setAEscena((x) => ({ ...x, [i]: e.target.value }))} style={{ fontSize: 12 }}>
                            {escenas.map((n) => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                        <div style={{ fontSize: 11, color: "var(--hi-tenue)", marginTop: 4 }}>Crea la versión siguiente del manuscrito con la escena bajo su título, como cambio marcado. Cuenta como texto de la IA.</div>
                        {llevado[i] && llevado[i].estado !== "pidiendo" && (
                          <div style={{ fontSize: 12, marginTop: 4, color: llevado[i].estado === "hecho" ? "var(--hi-bien-suave)" : "var(--hi-mal-suave)" }}>{(llevado[i] as { texto: string }).texto}</div>
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => insert(i, proposal)}
                      style={escenas.length > 0 && m.at
                        ? { marginTop: 8, background: "none", border: 0, padding: 0, color: "var(--hi-humano)", fontSize: 11, cursor: "pointer" }
                        : { marginTop: 8, background: "var(--hi-oficio)", color: "var(--hi-sobre-acento)", fontWeight: 800, border: 0, borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}
                    >
                      {inserted === i ? "✓ Insertado — revisa en Revisar" : escenas.length > 0 && m.at ? "o insertarla donde tengas el cursor" : enDoc ? `Insertar en «${enDoc}» como control de cambios` : "Insertar como control de cambios"}
                    </button>
                    <div style={{ fontSize: 11, color: "var(--hi-tenue)", marginTop: 4 }}>{escenas.length > 0 && m.at ? "Donde tengas el cursor entra con el estilo de ese párrafo." : "Entra donde tengas el cursor, y con el estilo de ese párrafo."}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {sending && (
          <div style={{ fontSize: 12, color: "var(--hi-tenue)" }}>
            {agent.name} está pensando{desde ? ` · ${Math.max(0, Math.round((ahora - desde) / 1000))} s` : "…"}
            {desde && ahora - desde > 45000 && <div style={{ fontSize: 11 }}>Las respuestas largas pueden tardar un par de minutos.</div>}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {err && <div style={{ fontSize: 12, color: "var(--hi-mal)", margin: "6px 0" }}>{err}</div>}

      {/* Entrada */}
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={2}
          placeholder={`Escribe a ${agent.name}… (Enter envía, Shift+Enter salto de línea)`}
          style={{ flex: 1, boxSizing: "border-box", padding: 8, borderRadius: 8, background: "var(--hi-papel)", color: "var(--hi-tinta)", border: "1px solid var(--hi-raya-2)", resize: "vertical", fontFamily: "inherit", fontSize: 12.5 }}
        />
        <button
          onClick={send}
          disabled={sending || !input.trim()}
          style={{ background: "var(--hi-humano)", color: "var(--hi-sobre-acento)", fontWeight: 800, border: 0, borderRadius: 8, padding: "0 14px", cursor: "pointer", opacity: sending || !input.trim() ? 0.5 : 1 }}
        >
          ▶
        </button>
      </div>

      {/* Instrucciones base + limpiar */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
        <button onClick={() => setShowInstr((s) => !s)} style={{ background: "none", border: 0, color: "var(--hi-humano)", fontSize: 11, cursor: "pointer", padding: 0 }}>
          Instrucciones base de {agent.name} {showInstr ? "▲" : "▼"}
        </button>
        <button onClick={resetChat} style={{ background: "none", border: 0, color: "var(--hi-apagado)", fontSize: 11, cursor: "pointer", padding: 0 }}>
          borrar conversación
        </button>
      </div>
      {showInstr && (
        <div style={{ marginTop: 6 }}>
          <textarea
            value={instr}
            onChange={(e) => setInstr(e.target.value)}
            rows={4}
            placeholder={`Cómo debe trabajar ${agent.name} en ESTE proyecto (p. ej. «nunca uses puntos suspensivos», «el tono es sobrio»…). Se guardan en la carpeta del proyecto y prevalecen sobre su oficio.`}
            style={{ width: "100%", boxSizing: "border-box", padding: 8, borderRadius: 8, background: "var(--hi-papel)", color: "var(--hi-tinta)", border: "1px solid var(--hi-raya-2)", resize: "vertical", fontFamily: "inherit", fontSize: 12 }}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
            <button onClick={saveInstr} style={{ background: "var(--hi-humano)", color: "var(--hi-sobre-acento)", fontWeight: 700, border: 0, borderRadius: 8, padding: "5px 12px", fontSize: 12, cursor: "pointer" }}>
              Guardar
            </button>
            {instrMsg && <span style={{ fontSize: 11, color: "var(--hi-tenue)" }}>{instrMsg}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
